import {
  EeClassWithValidation,
  EeClass,
  ProductInfo,
  PlanDetails,
  EeClassesTypes,
  ProductsInClass,
} from 'types/electionService';
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';

import uniqid from 'uniqid';
import { getEmployeeClasses, getProducts } from 'api/operations/electionService';

export default class EeClassesStore {
  rootStore: RootStore;
  loading = false;

  employeeClasses: EeClassWithValidation[] = [];
  employeeClassesWithEe: EeClass[] = [];

  products: ProductInfo[] = [];
  updatedProductsInClasses: ProductsInClass[] = [];

  showErrorToast = false;
  toastErrorMessage = '';

  saveTrigger = false; // trigger inputs to update their value in  the store
  checkedElementsAmount = 0; // show that all inputs were updated and trigger, if it is possible make validation check  and send request

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  public changeSavingTrigger(): void {
    this.setErrorToastStatus(false, '');
    this.checkedElementsAmount = 0; // reset checked fields count
    this.saveTrigger = !this.saveTrigger;
  }

  public setErrorToastStatus(status: boolean, message = ''): void {
    this.toastErrorMessage = message;
    this.showErrorToast = status;
  }

  public async getEeClasses(groupId: string, periodId: string): Promise<void> {
    this.setLoading(true);

    const classes = await getEmployeeClasses(groupId, periodId);
    runInAction(() => {
      this.employeeClasses = classes;
    });

    this.setLoading(false);
  }

  public async getEmployeeClassesWithEe(groupId: string, periodId: string): Promise<void> {
    this.setLoading(true);
    const res = await this.rootStore.electionStore.getEmployeeClassesWithEe(groupId, periodId);
    runInAction(() => {
      this.employeeClassesWithEe = res.data.getEmployeeClassesWithEe;
      this.setLoading(false);
    });
  }

  public async getChoices(groupId: string, periodId: string): Promise<void> {
    this.setLoading(true);
    const products = await getProducts(periodId);
    void runInAction(async () => {
      this.products = products;

      await this.getEeClasses(groupId, periodId);
      runInAction(() => {
        // for updating plans in classes
        const allProducts = this.products.map(({ plans }) => {
          const plansInPl = plans.map(({ id, eligibleClasses }: PlanDetails): ProductsInClass => {
            return { productId: id, employeeClasses: eligibleClasses };
          });
          return plansInPl;
        });

        this.updatedProductsInClasses = allProducts.flat();
      });
      this.setLoading(false);
    });
  }

  public addClass(type: EeClassesTypes, periodId: string, groupId: string): void {
    this.employeeClasses.push({
      name: '',
      type,
      periodId,
      groupId,
      id: `newClass_${type}_${uniqid()}`,
    });
  }

  public updateSelectedPlanInClass(productId: string, isProductSelected: boolean, className: string): void {
    const currentProduct = this.updatedProductsInClasses.find((product) => product.productId === productId);
    if (isProductSelected && currentProduct) {
      currentProduct.employeeClasses.push(className);
    }
    if (currentProduct && !isProductSelected) {
      const filteredClasses = currentProduct.employeeClasses.filter((eeClassName) => eeClassName !== className);
      currentProduct.employeeClasses = filteredClasses;
    }
  }

  public removeClass(classId: string): void {
    this.setErrorToastStatus(false);
    const updatedClasses = this.employeeClasses.filter(({ id }) => id !== classId);

    if (updatedClasses.length >= 1) {
      this.employeeClasses = updatedClasses;
    } else {
      this.setErrorToastStatus(true, 'You can`t remove all classes');
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private checkInputValidation(eeClassName: string, classId: string): string {
    const convertClassName = (name: string): string => {
      return name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    };

    const currentClass = this.employeeClasses.find(
      ({ name, id }) => convertClassName(name) === convertClassName(eeClassName) && id !== classId,
    );
    const isClassExist = !!currentClass;
    if (isClassExist) return 'A class with that name already exists';
    if (!eeClassName) return 'Invalid Class Name';
    return '';
  }

  public updateNewClass(
    classType: EeClassesTypes,
    className: string,
    epId: string,
    groupId: string,
    classId = '',
  ): void {
    this.checkedElementsAmount += 1;
    const currentClassIndex = this.employeeClasses.findIndex(({ id }) => classId === id);

    const errorMessage = this.checkInputValidation(className, classId);

    if (!errorMessage) {
      this.employeeClasses[currentClassIndex] = {
        name: className,
        type: classType,
        periodId: epId,
        groupId,
        id: `${epId}_${className}`,
        validationMessage: '',
      };
    }

    if (!className) {
      this.employeeClasses[currentClassIndex] = {
        name: className,
        type: classType,
        periodId: epId,
        groupId,
        id: `${epId}_${className}`,
        validationMessage: '',
      };
    }

    if (errorMessage || !className) {
      this.employeeClasses[currentClassIndex].validationMessage = errorMessage;
      this.setErrorToastStatus(true, 'There are some mistakes in your classes. Please fix them and resave');
    }

    const areClassesValid = this.employeeClasses.every(({ validationMessage }) => !validationMessage);

    if (areClassesValid) {
      this.setErrorToastStatus(false, '');
    }
  }

  public async saveClasses(groupId: string, epId: string): Promise<void> {
    this.setLoading(true);
    // remove validation field before saving classes. The ValidationMessage field is needed only for FE
    const updatedClasses: EeClass[] = this.employeeClasses.map(({ type, name, id, periodId }) => {
      return { type, name, id, periodId, groupId };
    });

    await this.rootStore.electionStore.updateEligibleClasses(groupId, updatedClasses, epId);
    this.setLoading(false);
  }

  public async saveProductClasses(groupId: string, periodId: string): Promise<void> {
    this.setLoading(true);
    await this.rootStore.electionStore.updateProductClasses(groupId, this.updatedProductsInClasses, periodId);
    this.setLoading(false);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
